import { useEffect, useState } from "react";
import { FormOverview_VM } from "./FormsOverview.component";
import { FormsListComponentState } from "../../../components/forms-overview/FormsListComponentState";
import { useServiceContext } from "../../../../contexts/ServiceContext";
import { useNavigate } from "react-router-dom";
import { Form, Page } from "../../../../core/entities/form";
import { usePageQuery } from "../../../../core/page-query/hooks/usePageQuery";
import { PageQuery } from "../../../../core/page-query/models/pageQuery";
import { SortBy } from "../../../../core/page-query/models/sortBy";

export function useFormsOverviewViewModel(): FormOverview_VM {
    const [listComponentState, setListComponentState] =
        useState<FormsListComponentState>();
    const { formService } = useServiceContext();
    const navigate = useNavigate();

    const [pageQuery, queryChanger] = usePageQuery();

    const [loading, setLoading] = useState<boolean>(false);
    const [canLoadMore, setCanLoadMore] = useState<boolean>(false);
    const [loadedPage, setLoadedPage] = useState<number>(-1);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const pageSize = 20;
    const [formPages, setPagesForms] = useState<{ [index: number]: Form[] }>(
        []
    );

    useEffect(() => {
        if (pageQuery && !isEmpty(pageQuery)) {
            fetchForms(pageQuery).then();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageQuery]);

    useEffect(() => {
        queryChanger.setPageQuery({
            ...pageQuery,
            page: {
                size: pageSize,
                index: 0
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function fetchForms(query: PageQuery) {
        if (loadedPage === -1) {
            setListComponentState(FormsListComponentState.Loading);
        }
        setLoading(true);
        await formService
            .getAllForms(query)
            .then((page) => {
                setListComponentState(
                    page.result.length === 0
                        ? FormsListComponentState.Empty
                        : FormsListComponentState.Content
                );
                handleNewPage(query.page.index, page);
                setLoading(false);
            })
            .catch(() => {
                setListComponentState(FormsListComponentState.Error);
                setLoading(false);
            });
    }

    function resetPaging() {
        setPagesForms({});
        setCanLoadMore(true);
        setListComponentState(undefined);
        setLoadedPage(-1);
        setTotalRecords(0);
    }

    function handleNewPage(pageIndex: number, page: Page<Form>) {
        setPagesForms((x) => ({
            ...x,
            [pageIndex]: page.result
        }));
        setCanLoadMore(page.hasNextPage);
        setLoadedPage(pageIndex);
        setTotalRecords(page.count);
    }

    async function queryData(filterFormModel: PageQuery) {
        resetPaging();
        queryChanger.setPageQuery({
            ...filterFormModel,
            page: {
                index: 0,
                size: pageSize
            }
        });
    }

    function handleLoadMore() {
        if (!canLoadMore) {
            return;
        }

        queryChanger.setPageQuery({
            ...pageQuery,
            page: {
                index: pageQuery.page.index + 1,
                size: pageSize
            }
        });
    }

    function handleFormRow(form: Form): void {
        navigate(`/forms/${form.id}`, {
            state: { prevPath: window.location.pathname }
        });
    }

    function onFilterReset() {
        resetPaging();
    }

    function handleSortChange(sortBy: SortBy) {
        window.scrollTo({ top: 0 });
        setCanLoadMore(false);
        queryChanger.handleSortByField({
            value: sortBy.field,
            sortDirection: sortBy.direction
        });
        queryChanger.handleNewPage({
            value: "0"
        });
    }

    let s: Form[] = [];
    for (let i = 0; i <= loadedPage; i++) {
        s = s.concat(formPages[i]);
    }
    return {
        listComponentState: listComponentState,
        onFilterReset: onFilterReset,
        onQueryChange: queryData,
        onFormRowClick: handleFormRow,
        forms: s,
        pq: pageQuery,
        onScrollEndReached: handleLoadMore,
        isLoading: loading,
        totalRecords: totalRecords,
        onSortChange: handleSortChange
    };
}

function isEmpty(obj: PageQuery) {
    if (obj.page) {
        if (obj.searchTerm) {
            return false;
        }
        if (obj.filterBy && obj.filterBy.length > 0) {
            return false;
        }
    }
    return true;
}
